/**
 * Cera Pro regional subset script ranges (runtime + build).
 * Latin excludes Vietnamese horn letters (U+01A0–01B0) and tone letters (U+1EA0–1EF9).
 * Those blocks are covered by self-hosted Noto Sans, not the Cera Vietnamese cut.
 */

/** @type {[number, number][]} */
export const CERA_REGIONAL_LATIN_RANGES = [
  [0x0020, 0x019f],
  [0x01b1, 0x024f],
  [0x0300, 0x036f],
  [0x2000, 0x206f], // dashes, curly quotes, ellipsis
  [0x20a0, 0x20cf],
  [0x2100, 0x215f],
  [0x2190, 0x219f],
  [0x2200, 0x22ff],
];

/** Horn letters (ơ/ư) + precomposed Vietnamese (U+1EA0–1EF9). Requires full Cera Pro masters. */
export const CERA_REGIONAL_VIETNAMESE_RANGES = [
  [0x01a0, 0x01b0],
  [0x1ea0, 0x1ef9],
];

/**
 * Combining acute (U+0301) is how Russian and Bulgarian mark stress (ма́ма).
 * It has to live in the same file as the Cyrillic letters, or the browser draws
 * the whole syllable with a fallback font.
 * @type {[number, number][]}
 */
export const CERA_REGIONAL_CYRILLIC_RANGES = [
  [0x0300, 0x036f],
  [0x0400, 0x04ff],
  [0x0500, 0x052f],
];

/** @type {[number, number][]} */
export const CERA_REGIONAL_GREEK_RANGES = [[0x0370, 0x03ff]];

/**
 * @param {[number, number][]} pairs
 * @returns {string}
 */
export function unicodeRangeFromPairs(pairs) {
  const hex = (cp) => cp.toString(16).toUpperCase().padStart(4, "0");
  return pairs.map(([a, b]) => (a === b ? `U+${hex(a)}` : `U+${hex(a)}-${hex(b)}`)).join(",");
}

/**
 * @param {string} range CSS unicode-range such as `U+0600-06FF,U+200C`
 * @returns {[number, number][]}
 */
export function pairsFromUnicodeRange(range) {
  return String(range || "")
    .split(",")
    .map((part) => part.trim().replace(/^U\+/i, ""))
    .filter(Boolean)
    .map((part) => {
      const [a, b = a] = part.split("-");
      return /** @type {[number, number]} */ ([parseInt(a, 16), parseInt(b, 16)]);
    });
}

export const UNICODE_RANGE_REGIONAL_LATIN = unicodeRangeFromPairs(CERA_REGIONAL_LATIN_RANGES);

export const UNICODE_RANGE_REGIONAL_VIETNAMESE = "U+01A0-01B0,U+1EA0-1EF9";

/** Sample UI string used to validate Vietnamese regional subsets after build. */
export const CERA_VIETNAMESE_COVERAGE_SAMPLE = "Từ vựng học tiếng Đức ơưă";

export const UNICODE_RANGE_REGIONAL_CYRILLIC = unicodeRangeFromPairs(CERA_REGIONAL_CYRILLIC_RANGES);

/** Monotonic Greek only. Cera Pro has no polytonic Greek (U+1F00–1FFF). */
export const UNICODE_RANGE_REGIONAL_GREEK = unicodeRangeFromPairs(CERA_REGIONAL_GREEK_RANGES);

/**
 * Drops every code point a Cera Pro face claims. Later `@font-face` rules win on overlap,
 * so Noto supplements must not claim characters Cera already draws.
 * @param {string} range
 * @returns {string}
 */
export function unicodeRangeWithoutCera(range) {
  const cera = [UNICODE_RANGE_REGIONAL_LATIN, UNICODE_RANGE_REGIONAL_CYRILLIC, UNICODE_RANGE_REGIONAL_GREEK]
    .flatMap(pairsFromUnicodeRange)
    .sort((x, y) => x[0] - y[0]);
  /** @type {[number, number][]} */
  const out = [];
  for (const [lo, hi] of pairsFromUnicodeRange(range)) {
    let start = lo;
    for (const [a, b] of cera) {
      if (b < start || a > hi) continue;
      if (a > start) out.push([start, a - 1]);
      start = Math.max(start, b + 1);
      if (start > hi) break;
    }
    if (start <= hi) out.push([start, hi]);
  }
  return unicodeRangeFromPairs(out);
}

/** @type {{ key: string, label: string, ranges: [number, number][] }[]} */
export const CERA_REGIONAL_SCRIPT_DEFS = [
  { key: "latin", label: "latin", ranges: CERA_REGIONAL_LATIN_RANGES },
  { key: "vietnamese", label: "vietnamese", ranges: CERA_REGIONAL_VIETNAMESE_RANGES },
  { key: "cyrillic", label: "cyrillic", ranges: CERA_REGIONAL_CYRILLIC_RANGES },
  { key: "greek", label: "greek", ranges: CERA_REGIONAL_GREEK_RANGES },
];

/**
 * @param {[number, number][]} pairs
 * @returns {string}
 */
export function glyphStringFromRanges(...pairs) {
  let out = "";
  for (const [a, b] of pairs) {
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    for (let cp = lo; cp <= hi; cp++) {
      try {
        out += String.fromCodePoint(cp);
      } catch {
        /* invalid scalar */
      }
    }
  }
  return out;
}

/**
 * @param {string} uiLang
 * @returns {"vietnamese"|"cyrillic"|"greek"|"arabic"|null}
 */
export function ceraRegionalSupplementKeyForUiLang(uiLang) {
  const base = String(uiLang || "")
    .trim()
    .toLowerCase()
    .split(/[-_]/)[0];
  if (base === "vi") return "vietnamese";
  if (base === "ar") return "arabic";
  if (base === "uk" || base === "ru") return "cyrillic";
  if (base === "el") return "greek";
  return null;
}
